import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GULL_PC_COLORS } from '../../../../component/card/line-bird-glyph';
import { PARASOL_PC_COLORS } from '../../../../component/card/line-parasol-glyph';
import { SNOW_PC_COLORS } from '../../../../component/card/line-snowflake-glyph';
import { JELLY_PC_COLORS } from '../../../../component/card/line-jellyfish-glyph';
import { SHELL_PC_COLORS } from '../../../../component/card/line-shell-glyph';
import { LAMP_PC_COLORS } from '../../../../component/card/line-lamp-glyph';
import { YACHT_PC_COLORS } from '../../../../component/card/line-yacht-glyph';
import { BASALT_PC_COLORS } from '../../../../component/card/line-basalt-glyph';
import { FLOWER_PC_COLORS } from '../../../../component/card/line-meadow-flower-glyph';
import { VIOLIN_PC_COLORS } from '../../../../component/card/line-violin-glyph';
import { CLAVEL_PC_COLORS } from '../../../../component/card/line-clavel-glyph';
import { VOLCANO_PC_COLORS } from '../../../../component/card/line-volcano-glyph';
import { PAGODA_PC_COLORS } from '../../../../component/card/line-pagoda-glyph';
import { lineColorSet } from '../../../../component/card/line-color-set';
import { mixHex } from '../../../../component/card/line-electric-colors';
import { LineThumbnailVariant } from '../../../../component/card/line-thumbnail/line-thumbnail.component';
import { THEMES } from '../../../../data/product/product.data';
import { Product, ProductLine } from '../../../../model/product/product.model';

/**
 * Private LINE-theme preview gallery.
 *
 * This page is NOT linked from the nav or sitemap and ships `noindex` (see
 * scripts/seo-route-meta.js), it exists so the Codigrate team can capture a
 * clean LINE chat preview per theme. It is a thin shell that renders one
 * `<app-line-card>` per theme; the card owns the entire chat-mockup design.
 *
 * Capture mode: with `?only=<product-key>` the page renders just that one
 * theme's preview, so a screenshot tool can grab a single phone per theme.
 */
@Component( {
  selector: 'app-line-page',
  templateUrl: './line-page.component.html',
  styleUrls: [ './line-page.component.scss' ],
} )
export class LinePageComponent implements AfterViewInit, OnDestroy {

  /** Theme product lines, iterated in the template. */
  themes: ProductLine[] = THEMES;

  /** When set (via `?only=`), only this product's section is rendered. */
  onlyKey: string | null;

  /**
   * Sidebar nav (owner 2026-08-15): the board is twenty full-height theme
   * sections, so a fixed rail lists every theme and jumps to its board on
   * click. The rail mirrors the render order and drops the all-in-one bundle,
   * which has no LINE theme, exactly like the boards do.
   */
  get navLines(): { name: string; products: Product[] }[] {
    return this.themes
      .map( line => ( {
        name: line.name,
        products: ( line.products || [] ).filter( p => p.key !== 'all-in-one' ),
      } ) )
      .filter( line => line.products.length > 0 );
  }

  /** Product key of the board currently under the viewport's middle band. */
  activeKey = '';

  /** rAF guard so a scroll burst costs one measurement per frame. */
  private railTicking = false;

  /** The rail's dot for a theme: its first palette colour. */
  navDot( product: Product ): string {
    return product.compactPalette?.[ 0 ]
      || product.designTokens?.primaryColor
      || '#8aa0ad';
  }

  /**
   * Rail click: bring that theme's board to the top of the viewport.
   *
   * `scrollIntoView` rather than `window.scrollTo`, because on this layout the
   * element that scrolls is BODY (the shell caps html at 100%), so a window
   * scroll is a no-op. The header gap comes from `scroll-margin-top` on the
   * board itself.
   *
   * The jump is INSTANT on purpose: `behavior: 'smooth'` measured as a silent
   * no-op here (boards sit up to ~33k px apart, and nothing moved at all),
   * and a review rail that sometimes does nothing is worse than one that jumps.
   */
  scrollToTheme( key: string ): void {
    const el = this.host.nativeElement
      .querySelector( `.theme-board[data-key="${ key }"]` ) as HTMLElement | null;
    if ( !el ) { return; }
    this.activeKey = key;
    el.scrollIntoView( { block: 'start' } );
  }

  /**
   * When set (via `?asset=`), the page renders ONLY that single asset at its
   * native pixel size (no headers / no other tiles), so a 1:1 PNG can be
   * exported. `menu-bg` => the LINE menu background (1472x150).
   */
  captureAsset: string | null;

  /**
   * Passcode capture params (`?asset=passcode&pc=<0..7>&sz=<120|116>`): which of
   * the 8 dots (i_12..i_19 order) to render and at what native px. iOS dots are
   * 120, Android 116. Defaults render the first dot at 120.
   */
  capturePcIndex = 0;
  captureSize = 120;

  /**
   * Thumbnail capture variant (`?asset=thumbnails&v=<store|ios|android>`):
   * which main image to render at its native px. Defaults to the store one.
   */
  captureVariant: LineThumbnailVariant = 'store';

  /** The three thumbnail variants, in the generator's export order. */
  readonly thumbnailVariants: LineThumbnailVariant[] = [ 'store', 'ios', 'android' ];

  /**
   * Menu-button capture (`?asset=menu-buttons&mb=<0..17>`): which of the 18
   * tab icons (menuButtons order: chats OFF/ON, calls OFF/ON, …) to render at
   * its native 128×150.
   */
  captureMbIndex = 0;

  /**
   * Profile capture (`?asset=profile&pf=<person|group>&sz=<240|247>`): which
   * avatar (i_20 person / i_21 group) at what native px (iOS 240 / Android 247).
   */
  captureProfileKind: 'person' | 'group' = 'person';

  /**
   * The full set of images LINE wants for a theme (mirrors the groups in
   * line-themes/generate-line-theme.py). Each row is one asset group we render
   * + tune on this page and tick off as it is finished. `wide` lays the group
   * out on its own full-width row; `built` flips the tick to done.
   */
  readonly lineAssets: {
    key: string; title: string; note: string; sizes: string; wide: boolean; files: string[];
  }[] = [
    { key: 'previews',     title: 'Previews',        note: 'iOS 26 Home · Home · Chat · Passcode', sizes: 'live theme render',              wide: true,
      files: [] },
    { key: 'thumbnails',   title: 'Main images',     note: 'Store · iOS · Android thumbnails', sizes: '198×278 · 200×284 · 136×202',        wide: false,
      files: [ 'store_thumbnail.png', 'ios_thumbnail.png', 'android_thumbnail.png' ] },
    { key: 'menu-bg',      title: 'Menu background', note: 'Behind the bottom tab bar',         sizes: '1472×150',                          wide: true,
      files: [] },
    // Passcode sits ABOVE the menu buttons on the board (owner 2026-07-24) so
    // the actively-tuned dot designs are reviewed without scrolling past the
    // 18 + 18 tab icons.
    { key: 'passcode',     title: 'Passcode',        note: '4 dots · OFF + ON',                sizes: 'iOS 120 · Android 116',             wide: false,
      files: [ 'i_12.png', 'i_13.png', 'i_14.png', 'i_15.png', 'i_16.png', 'i_17.png', 'i_18.png', 'i_19.png' ] },
    { key: 'menu-buttons', title: 'Menu buttons',    note: '9 tab icons · OFF + ON',           sizes: '128×150',                           wide: false,
      files: [ 'i_03.png', 'i_04.png', 'i_07.png', 'i_08.png', 'i_25.png', 'i_26.png', 'i_27.png', 'i_28.png', 'i_29.png', 'i_30.png', 'i_31.png', 'i_32.png', 'i_33.png', 'i_34.png', 'i_35.png', 'i_36.png', 'i_37.png', 'i_38.png' ] },
    // iOS 26 Liquid Glass tab icons (the i_XX_g slots, LINE 26.12+/iOS 26): the
    // system draws the glass tab background, the PNG is only the glyph, so it
    // must read on both black and white. Without these the theme gets the
    // "partial iOS 26 support" badge and default menu icons.
    { key: 'menu-buttons-g', title: 'Menu buttons · iOS 26', note: '9 tab icons · OFF + ON · Liquid Glass', sizes: '80×56', wide: false,
      files: [ 'i_03_g.png', 'i_04_g.png', 'i_07_g.png', 'i_08_g.png', 'i_25_g.png', 'i_26_g.png', 'i_27_g.png', 'i_28_g.png', 'i_29_g.png', 'i_30_g.png', 'i_31_g.png', 'i_32_g.png', 'i_33_g.png', 'i_34_g.png', 'i_35_g.png', 'i_36_g.png', 'i_37_g.png', 'i_38_g.png' ] },
    { key: 'profile',      title: 'Profile',         note: 'Person + group avatar',            sizes: 'iOS 240 · Android 247',             wide: false,
      files: [ 'i_20.png', 'i_21.png' ] },
    { key: 'chat-bg',      title: 'Chat background', note: 'Theme wallpaper',                  sizes: 'iOS 1482×1334 · Android 1300×1300', wide: false,
      files: [ 'i_22.png' ] },
  ];

  /**
   * Which assets are FINISHED, PER THEME. A tick is the green --success check
   * only for the theme+asset pairs listed here; every other asset stays an open
   * --error ring. Done work is tracked per theme, e.g. Aurora's passcode is
   * done, Everest's is not. Add a theme's asset key here as it is finished.
   */
  readonly doneAssets: { [ themeKey: string ]: string[] } = {
    'aurora-borealis': [ 'thumbnails', 'menu-bg', 'menu-buttons', 'menu-buttons-g', 'passcode', 'profile' ],
    'everest': [ 'thumbnails', 'menu-bg', 'menu-buttons', 'menu-buttons-g' ],
  };

  /**
   * Assets finished for EVERY theme at once, their tick is green on every
   * board without per-theme bookkeeping. The chat background (theme wallpaper)
   * is done across all themes.
   */
  readonly doneForAllThemes: string[] = [ 'chat-bg' ];

  /**
   * Owner approvals clicked LIVE on the board (the tick circles are buttons):
   * an EXPLICIT per-key override kept in localStorage under `lineDoneAssets`, so
   * the review loop works both ways — the owner clicks a tick and the reviewer
   * (Claude) reads the same key back from the browser. Baked into `doneAssets`
   * in code once a theme's pass is finalized.
   *
   * `true` ticks green, `false` CLEARS a tick that `doneAssets` /
   * `doneForAllThemes` declare done (owner 2026-08-12 — the old shape was a list
   * of approvals, which could only ever add, so a code-declared tick was stuck
   * green and un-approving was impossible). Keys are either a group key
   * ('menu-bg') or one asset inside a group ('menu-buttons:5', see assetItemKey).
   */
  localDone: { [ themeKey: string ]: { [ assetKey: string ]: boolean } } = {};

  /** The tick key for ONE tile inside a group row, so each asset in a row is
   *  approved on its own instead of the whole row moving together
   *  (owner 2026-08-12). Index = the tile's position in that row. */
  assetItemKey( groupKey: string, index: number ): string {
    return `${ groupKey }:${ index }`;
  }

  /** True when this theme's asset is finished (drives the green tick). */
  isAssetDone( productKey: string, assetKey: string ): boolean {
    const overrides = this.localDone[ productKey ];
    if ( overrides && Object.prototype.hasOwnProperty.call( overrides, assetKey ) ) {
      return overrides[ assetKey ];
    }
    // Until a tile is clicked it inherits whatever its GROUP is declared as.
    const groupKey = assetKey.split( ':' )[ 0 ];
    return this.doneForAllThemes.includes( groupKey )
      || ( this.doneAssets[ productKey ] || [] ).includes( groupKey );
  }

  /** Tick click: flip this asset's approval and persist it for the reviewer. */
  toggleAssetDone( productKey: string, assetKey: string ): void {
    const overrides = this.localDone[ productKey ] || ( this.localDone[ productKey ] = {} );
    overrides[ assetKey ] = !this.isAssetDone( productKey, assetKey );
    try {
      localStorage.setItem( 'lineDoneAssets', JSON.stringify( this.localDone ) );
    } catch ( e ) { /* storage unavailable (SSR/private mode) — view-only */ }
  }

  /**
   * The chat-background scrim baked over the shipped wallpaper asset, so the
   * board tile previews exactly what the generator bakes: a dark wash on dark
   * themes, a white frosted-glass wash on LIGHT themes. Mirrors line.js
   * coverFromBackground and the line-card preview.
   */
  chatScrim( product: Product ): string {
    const light = ( product.appearance || '' ).toLowerCase() === 'light';
    return light ? 'rgba(255, 255, 255, 0.34)' : 'rgba(0, 0, 0, 0.25)';
  }

  /**
   * Flat color-skin "sky" the transparent kawaii chat wallpaper floats over
   * (kept in sync with line-chat-bg's skinColor). Computed here so the board
   * tile's background does not depend on the child component's field mid-CD.
   */
  chatSky( product: Product ): string {
    // The flat "sky" the wallpaper composites over — must equal the chat skin
    // each theme actually renders (line-chat-bg.skinColor / line-card's chat
    // surface), so the transparent asset previews exactly like the phone.
    // These MUST match line-card.skinColor case for case. They drifted once
    // already (owner 2026-08-12): Everest and Aurora both moved to one of LINE's
    // own skins and only line-card was updated, so the board tile previewed the
    // wallpaper over a colour the phone never draws.
    if ( product.key === 'tokyo' ) { return '#B99EC5'; }            // mauve chat skin
    if ( product.key === 'everest' ) { return '#FFFFFF'; }          // LINE default skin
    if ( product.key === 'aurora-borealis' ) { return '#131313'; }  // near-black skin
    if ( product.key === 'sequoia' ) { return '#EBEBDE'; }          // the skin's pale chat sky
    if ( product.key === 'autumn' ) { return '#FFF1DD'; }           // the skin's cream chat sky
    if ( product.key === 'roraima' ) { return '#5C3E30'; }          // the skin's dark-brown chat sky (sampled)
    if ( product.key === 'miami' ) { return '#FFF2F6'; }            // the skin's palest pink talk room
    if ( product.key === 'istanbul' ) { return '#F2FDFF'; }         // the skin's very pale cyan chat body (sampled)
    if ( product.key === 'paris' ) { return '#FFF1F2'; }            // the skin's near-white pink talk room (sampled)
    if ( product.key === 'rio-de-janeiro' ) { return '#ECECEC'; }   // the skin's neutral grey chat sky (sampled)
    if ( product.key === 'tallinn' ) { return '#D3EAF2'; }          // the skin's pale winter chat sky (sampled)
    if ( product.key === 'ocean' ) { return '#EDFAF9'; }            // the skin's palest sea-mist talk room (sampled)
    if ( product.key === 'salda' ) { return '#FFF8E8'; }            // the skin's cream talk room (sampled)
    if ( product.key === 'prague' ) { return '#131313'; }           // the skin's near-black talk room (sampled)
    if ( product.key === 'sydney' ) { return '#D3EAF2'; }           // the skin's pale-blue talk room (sampled)
    if ( product.key === 'reynisfjara' ) { return '#131313'; }      // the skin's near-black talk room (sampled)
    if ( product.key === 'spring' ) { return '#FFFFFF'; }            // the skin's white talk room (sampled)
    if ( product.key === 'vienna' ) { return '#F8EDE7'; }           // the skin's warm blush talk room (sampled)
    if ( product.key === 'madrid' ) { return '#FFDD66'; }           // the skin's one saffron, talk room and all (sampled)
    if ( product.key === 'etna' ) { return '#131313'; }             // LINE's own near-black, talk room and all (sampled)
    if ( product.key === 'fuji' ) { return '#FFE8E1'; }             // the skin's blush talk room (sampled)
    const t: any = product.designTokens || {};
    return mixHex( '#FFFFFF', t.primaryColor || '#E898B4', 0.10 );
  }

  /**
   * Background behind the passcode dots in the ASSET section, so the white OFF
   * blossoms are visible there too. Sakura uses its real passcode upper-zone
   * pink, Everest the white one LINE's default skin paints there; other themes
   * fall back to a neutral dark tile.
   */
  passcodeBg( product: Product ): string {
    if ( product.key === 'sakura' ) { return '#FEA5B9'; }
    if ( product.key === 'everest' ) { return '#FFFFFF'; }
    if ( product.key === 'sequoia' ) { return '#EFEFD9'; }
    if ( product.key === 'autumn' ) { return '#FFF1DD'; }
    if ( product.key === 'roraima' ) { return '#422C21'; }
    if ( product.key === 'miami' ) { return '#FC5B96'; }
    if ( product.key === 'istanbul' ) { return '#4AC3E0'; }
    // Paris is the family's first INVERTED passcode: its dots sit on the PALE
    // upper zone, not on a coloured block, so the board reviews them against that.
    if ( product.key === 'paris' ) { return '#FFDADA'; }
    // Rio's dots sit on the skin's WHITE upper zone, so the board reviews them
    // there — and it is why its statues are the dark ones in this family.
    if ( product.key === 'rio-de-janeiro' ) { return '#FFFFFF'; }
    // Tallinn's dots sit on the WHITE upper zone of its passcode screen.
    if ( product.key === 'tallinn' ) { return '#FFFFFF'; }
    // Ocean's dots sit on the BRIGHT TEAL upper zone of its passcode screen
    // (sampled). It was a dark zone until the owner's screenshots arrived, which
    // is what flipped its jellies from lit creatures to dark ones.
    if ( product.key === 'ocean' ) { return '#2FB8A7'; }
    // Salda's shells lie on the CREAM upper zone (sampled). It was flipped to a
    // deep lake for one round so pale shells would have something to sit on; the
    // phone draws cream, so the shells are the dark marks instead.
    if ( product.key === 'salda' ) { return '#FFF8E8'; }
    // Prague's lamps light up on the skin's near-black, which is the WHOLE
    // passcode screen here (sampled) rather than a deeper upper zone — this skin
    // does not split it.
    if ( product.key === 'prague' ) { return '#131313'; }
    // Sydney's buildings stand on the PALE HARBOUR upper zone, so they are dark.
    if ( product.key === 'sydney' ) { return '#FFFFFF'; }   // its boats sit on the WHITE upper zone (sampled)
    // Reynisfjara's stacks stand on the DEEP dark upper zone and light up on it.
    if ( product.key === 'reynisfjara' ) { return '#131313'; }      // one zone, sampled — this skin does not split the passcode screen
    // Spring's birds hover on the PALE MEADOW upper zone, so they are the dark ones.
    if ( product.key === 'spring' ) { return '#FFFFFF'; }            // its bells sit on the WHITE upper zone (sampled)
    // Vienna's violins lie on the DEEP wine upper zone and warm up on it.
    if ( product.key === 'vienna' ) { return '#F8EDE7'; }   // sampled: the lock screen's upper zone is the talk-room blush
    // Madrid's fans sit on the WARM CREAM upper zone, so they are the dark ones.
    if ( product.key === 'madrid' ) { return '#FEF9E8'; }   // sampled: the lock screen's cream upper zone
    // Etna's cones stand on the NEAR-BLACK upper zone and heat up on it.
    if ( product.key === 'etna' ) { return '#131313'; }   // sampled: ONE zone, the whole lock screen is this
    // Fuji's pagodas stand on the PALE BLUSH upper zone, so they are the dark ones.
    if ( product.key === 'fuji' ) { return '#FFE8E1'; }   // sampled: the lock screen's blush upper zone
    return '#2A2E33';
  }

  /**
   * Board-header name colour. Some themes' primaryForeground sits too close
   * to their editorBackground (the board's paint), so the big name washes
   * out; the base set's fg is picked for contrast against light/dark.
   */
  boardFg( product: Product ): string {
    // The board's own title sits on the dark workbench UI, NOT a preview surface,
    // so use the appearance-neutral fg (white on dark themes) — never the
    // per-theme LINE base set, whose light mauve (Tokyo) vanishes on the board.
    return lineColorSet( undefined, product.appearance ).fg;
  }

  /** Path to a generated LINE asset PNG (copied from line-themes). */
  assetSrc( productKey: string, file: string ): string {
    return `assets/line-assets/${ productKey }/${ file }`;
  }

  /**
   * The 18 tab buttons drawn LIVE (app-line-menu-button) instead of generator
   * PNGs: OFF then ON per slot, in the generator's slot order (i_03 chats,
   * i_07 calls, i_25 news, i_27 wallet, i_29 home, i_31 today, i_33 voom,
   * i_35 shopping, i_37 mini).
   */
  readonly menuButtons: { name: string, on: boolean }[] =
    [ 'chats', 'calls', 'news', 'wallet', 'home', 'today', 'voom', 'shopping', 'mini' ]
      .reduce( ( out, name ) => out.concat( [ { name, on: false }, { name, on: true } ] ),
               [] as { name: string, on: boolean }[] );

  /**
   * DISPLAY order for the menu-buttons asset section (owner 2026-07-29): Home
   * first, then all the ON glyphs, then all the OFF glyphs. Capture still uses
   * the raw `menuButtons` order (tied to the slot/file numbering), so this only
   * changes how the tiles are laid out for review.
   */
  get menuButtonsDisplay(): { name: string, on: boolean }[] {
    const homeFirst = [ ...this.menuButtons ].sort(
      ( a, b ) => ( a.name === 'home' ? 0 : 1 ) - ( b.name === 'home' ? 0 : 1 ) );
    return [ ...homeFirst.filter( b => b.on ), ...homeFirst.filter( b => !b.on ) ];
  }

  /**
   * The 8 passcode dots in LINE's order (i_12..i_19): OFF then ON per digit.
   * The 4 ON dots take the theme's colour circles 2..5 (compactPalette[1..4]),
   * matching the passcode in generate-line-theme.py.
   */
  passcodeDots( product: Product ): { on: boolean; color: string; off: string; seed: number }[] {
    const cp = ( product.compactPalette?.length ? product.compactPalette : product.palette ) || [];
    const off = product.designTokens?.secondaryForeground || '#85A2B3';
    // Tokyo star dots are light-yellow glowing twinkles (owner 2026-08-05, tower
    // retired); others cycle through colour circles 2..5.
    // Miami's palms are all ONE neon green (owner 2026-08-13) rather than one per
    // digit: Electric Aqua, the green of the theme's COLOUR CIRCLE (compactPalette
    // index 3). Deep Lime was the first pick and is wrong — it lives in the wide
    // palette, not in the five circles the site and these assets work from.
    const cols = product.key === 'tokyo'
      ? [ '#FFF0BE', '#FFF3CE', '#FFEAA6', '#FFE49C' ]
      : product.key === 'miami'
        ? [ '#00D1C1', '#00D1C1', '#00D1C1', '#00D1C1' ]
      // Istanbul's four gulls share one entered colour, measured on the cyan
      // passcode zone (line-bird-glyph). Read from the same constant line-lock
      // uses, so the preview and the exported PNG cannot drift.
      : product.key === 'istanbul'
        ? GULL_PC_COLORS
      // Rio's four parasols are identical, two colours held in the glyph module.
      // Same constant line-lock reads.
      : product.key === 'rio-de-janeiro'
        ? PARASOL_PC_COLORS
      // Tallinn's four flakes, one drawing in four ice tints. Same constant
      // line-lock reads.
      : product.key === 'tallinn'
        ? SNOW_PC_COLORS
      // Ocean's four jellies, one drawing in four bioluminescent hues. Same
      // constant line-lock reads, so the board and the phone cannot disagree.
      : product.key === 'ocean'
        ? JELLY_PC_COLORS
      // Salda's four shells, one drawing walking from the shallows to the deep.
      : product.key === 'salda'
        ? SHELL_PC_COLORS
      // Prague's four lamps, one drawing walking from cold iron to full gold.
      : product.key === 'prague'
        ? LAMP_PC_COLORS
      // Sydney's four, walking from the shallows to the deep and then ashore.
      : product.key === 'sydney'
        ? YACHT_PC_COLORS
      // Reynisfjara's four, made of light rather than of hue.
      : product.key === 'reynisfjara'
        ? BASALT_PC_COLORS
      // Spring's four, opening bottom to top the way a spike of bells does.
      : product.key === 'spring'
        ? FLOWER_PC_COLORS
      // Vienna's four, DARKENING from fresh honey to old rosewood — on a pale skin
      // a mark lights up by getting darker, which is the reverse of what this ramp
      // used to do.
      : product.key === 'vienna'
        ? VIOLIN_PC_COLORS
      // Madrid's four, all RED — one hue separating four times, which is a tighter
      // brief than any other theme here and needs a wider spread to manage it.
      : product.key === 'madrid'
        ? CLAVEL_PC_COLORS
      // Etna's four, heating from cooled basalt to the orange at the vent.
      : product.key === 'etna'
        ? VOLCANO_PC_COLORS
      // Fuji's four, walking ACROSS a dawn rather than down one hue.
      : product.key === 'fuji'
        ? PAGODA_PC_COLORS
      : [ cp[ 1 ], cp[ 2 ], cp[ 3 ], cp[ 4 ] ];
    const dots: { on: boolean; color: string; off: string; seed: number }[] = [];
    for ( let i = 0; i < 4; i++ ) {
      const c = cols[ i ] || '#888888';
      // OFF and ON of a digit share the colour (OFF is just the hollow ring);
      // `seed` is the digit index, driving the sakura blossom's per-flower look.
      dots.push( { on: false, color: c, off, seed: i } );
      dots.push( { on: true, color: c, off, seed: i } );
    }
    return dots;
  }

  /** Passcode variant per theme: Everest = the restart's lit summit, Sakura =
   *  kawaii cherry blossom, everything else = the plain themed dot.
   *  Must stay in step with line-lock's variant — the lock preview is what the
   *  marketplace screenshot shows, and this is what the shipped PNG holds. */
  passcodeVariant( product: Product ): 'dot' | 'mountain' | 'blossom' | 'tower' | 'star' | 'peak' | 'orb' | 'tree' | 'maple' | 'tepui' | 'tumbleweed' | 'sun' | 'palm' | 'gull' | 'eiffel' | 'parasol' | 'snowflake' | 'jellyfish' | 'shell' | 'lamp' | 'yacht' | 'basalt' | 'flower' | 'violin' | 'clavel' | 'volcano' | 'fuji' {
    if ( product.key === 'everest' ) { return 'peak'; }
    if ( product.key === 'aurora-borealis' ) { return 'orb'; }
    if ( product.key === 'sequoia' ) { return 'tree'; }
    if ( product.key === 'autumn' ) { return 'maple'; }
    if ( product.key === 'roraima' ) { return 'sun'; }
    if ( product.key === 'miami' ) { return 'palm'; }
    // Istanbul = the seagull (owner 2026-08-14), one drawing in four colours.
    if ( product.key === 'istanbul' ) { return 'gull'; }
    if ( product.key === 'paris' ) { return 'eiffel'; }
    if ( product.key === 'rio-de-janeiro' ) { return 'parasol'; }
    if ( product.key === 'tallinn' ) { return 'snowflake'; }
    if ( product.key === 'ocean' ) { return 'jellyfish'; }
    if ( product.key === 'salda' ) { return 'shell'; }
    if ( product.key === 'prague' ) { return 'lamp'; }
    if ( product.key === 'sydney' ) { return 'yacht'; }
    if ( product.key === 'reynisfjara' ) { return 'basalt'; }
    if ( product.key === 'spring' ) { return 'flower'; }
    if ( product.key === 'vienna' ) { return 'violin'; }
    if ( product.key === 'madrid' ) { return 'clavel'; }
    if ( product.key === 'etna' ) { return 'volcano'; }
    if ( product.key === 'fuji' ) { return 'fuji'; }
    if ( product.key === 'sakura' ) { return 'blossom'; }
    if ( product.key === 'tokyo' ) { return 'star'; }
    return 'dot';
  }

  constructor( route: ActivatedRoute, private host: ElementRef<HTMLElement>, private zone: NgZone ) {
    this.onlyKey = route.snapshot.queryParamMap.get( 'only' );
    // Restore the owner's clicked-tick approvals (see toggleAssetDone). Stored
    // boards from before 2026-08-12 hold a LIST of approved keys per theme
    // instead of a key->boolean map; read those as all-true so nobody's earlier
    // clicks are dropped by the shape change.
    try {
      const stored = JSON.parse( localStorage.getItem( 'lineDoneAssets' ) || '{}' );
      for ( const themeKey of Object.keys( stored ) ) {
        const v = stored[ themeKey ];
        this.localDone[ themeKey ] = Array.isArray( v )
          ? v.reduce( ( m: { [ k: string ]: boolean }, k: string ) => ( m[ k ] = true, m ), {} )
          : ( v || {} );
      }
    } catch ( e ) {
      this.localDone = {};
    }

    this.captureAsset = route.snapshot.queryParamMap.get( 'asset' );
    const pc = route.snapshot.queryParamMap.get( 'pc' );
    if ( pc !== null ) { this.capturePcIndex = +pc; }
    const sz = route.snapshot.queryParamMap.get( 'sz' );
    if ( sz !== null ) { this.captureSize = +sz; }
    const v = route.snapshot.queryParamMap.get( 'v' );
    if ( v === 'store' || v === 'ios' || v === 'android' ) { this.captureVariant = v; }
    const mb = route.snapshot.queryParamMap.get( 'mb' );
    if ( mb !== null ) { this.captureMbIndex = +mb; }
    const pf = route.snapshot.queryParamMap.get( 'pf' );
    if ( pf === 'person' || pf === 'group' ) { this.captureProfileKind = pf; }
    // In capture mode hide the whole layout chrome (nav / footer / hero svg) so
    // a headless screenshot of the top-left is purely the asset.
    if ( this.captureAsset && typeof document !== 'undefined' ) {
      document.documentElement.classList.add( 'line-asset-capture' );
    }
  }

  /**
   * Highlight whichever board is being read, from a scroll listener rather than
   * an IntersectionObserver: on this layout the element that scrolls is BODY
   * (the shell caps html at 100%), and the observer never reports an
   * intersection because the implicit root's scroll position stays at zero.
   *
   * The listener is registered OUTSIDE Angular (this page renders twenty-plus
   * live theme boards, so a change-detection pass per scroll event is far too
   * expensive) and re-enters the zone only when the highlighted theme actually
   * changes. Skipped in capture / single-theme modes, where there is no rail.
   */
  ngAfterViewInit(): void {
    if ( this.captureAsset || this.onlyKey || typeof document === 'undefined' ) { return; }
    this.zone.runOutsideAngular( () => {
      // Capture phase: with BODY as the scroller the event does not reach
      // window in the bubble phase on every engine.
      document.addEventListener( 'scroll', this.onScroll, { passive: true, capture: true } );
    } );
    // Deferred one macrotask: setting a bound field straight from
    // ngAfterViewInit lands after this change-detection pass, so the rail would
    // only light up on the first scroll.
    setTimeout( () => this.syncActiveBoard() );
  }

  /** Scroll handler: one measurement per animation frame, not per event. */
  private onScroll = (): void => {
    if ( this.railTicking ) { return; }
    this.railTicking = true;
    requestAnimationFrame( () => {
      this.railTicking = false;
      this.syncActiveBoard();
    } );
  };

  /**
   * The active board is the last one whose top has passed the viewport's upper
   * third, which is the board filling the screen while reading.
   */
  private syncActiveBoard(): void {
    const boards = this.host.nativeElement.querySelectorAll<HTMLElement>( '.theme-board[data-key]' );
    const line = window.innerHeight * 0.35;
    let key = '';
    boards.forEach( board => {
      if ( board.getBoundingClientRect().top <= line ) {
        key = board.getAttribute( 'data-key' ) || key;
      }
    } );
    if ( !key || key === this.activeKey ) { return; }
    this.zone.run( () => {
      this.activeKey = key;
      this.revealActiveInRail();
    } );
  }

  /**
   * Keep the highlighted theme visible in the rail: the list is taller than the
   * rail on most screens, so scrolling down to a late theme would otherwise
   * highlight a row that is out of sight. `nearest` is a no-op when the row is
   * already visible, so the page itself never moves.
   */
  private revealActiveInRail(): void {
    setTimeout( () => {
      const active = this.host.nativeElement.querySelector( '.board-nav-item.is-active' );
      active?.scrollIntoView( { block: 'nearest' } );
    } );
  }

  ngOnDestroy(): void {
    if ( typeof document !== 'undefined' ) {
      document.removeEventListener( 'scroll', this.onScroll, { capture: true } );
    }
    if ( typeof document !== 'undefined' ) {
      document.documentElement.classList.remove( 'line-asset-capture' );
    }
  }

  /** trackBy for the product-line *ngFor. */
  trackByLineKey( _index: number, line: ProductLine ): string {
    return line.key;
  }

  /** trackBy for the product *ngFor (product key is unique per render). */
  trackByProductKey( _index: number, product: { key: string } ): string {
    return product.key;
  }
}
